import { Alert } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { AngleLeftLightIcon } from '@mm/icons';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import campaignPurposesCRUDActions from '@mm/state/actions/CampaignPurposesCRUDActions';
import campaignsCRUDActions, {
  useCampaignCRUDActions,
} from '@mm/state/actions/CampaignsCRUDActions';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import pageCRUDActions, {
  CreatePageParams,
} from '@mm/state/actions/PageCRUDActions';
import { CampaignPurpose } from '@mm/state/interfaces/campaignPurposes';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import {
  CampaignAcceptableTime,
  CampaignAudienceAttribute,
  CampaignSchedulerAttribute,
  CampaignStatuses,
  CampaignType,
  PlatformType,
} from '@mm/state/model/CampaignsModel';
import { PageKind } from '@mm/state/model/PageModel';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  CallToActionType,
  CampaignDisplayType,
  GeneralConfirmModalProps,
} from '@mm/state/modules/modalsState/props';
import {
  campaignsWithMappedPurposeAndDisplayStatusSelector,
  CampaignTypeWithMappedPurposeAndDisplayStatus,
} from '@mm/state/selectors/campaignSelectors';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
} from '@mm/state/selectors/facebookPageSelector';
import {
  combinedFeatureFlagsSelector,
  companyFeatureFlagsSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { welcomePageSelector } from '@mm/state/selectors/onboardingSelectors';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import {
  activeChatbotSelector,
  getTwilioAvailable,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import {
  HandlerMethodTypes,
  LifeCycleThis,
  StateUpdaterMethodTypes,
} from '@mm/utils';
import { ChannelName } from '@mm/state/model/ChatbotModel';
import { Route, Switch } from 'react-router';
import useRouter from 'use-react-router';
import classNames from 'classnames';
import { Location } from 'history';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { connect, useSelector } from 'react-redux';
import { Prompt, useHistory, withRouter } from 'react-router-dom';
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withStateHandlers,
} from 'recompose';
import { Dispatch } from 'redux';
import TextInput from '../../../../../common/components/TextInput';
import { EVERY_DAY } from '../../../../../common/helpers/date-formatting';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { reorder } from '../../../../../common/helpers/utils/sorting';
import { getDeviceInfo } from '../../../../../common/hooks/use-device-info';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { useRouteParams } from '../../../beta/hooks/use-route-params';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import CampaignQueueModal from '../../components/CampaignIndexRow/CampaignQueueModal/CampaignQueueModal';
import CampaignStatusIcon from '../../components/CampaignIndexRow/CampaignStatusIcon/CampaignStatusIcon';
import ConfirmEndModal from '../../components/ConfirmEndModal/ConfirmEndModal';
import {
  PROMOTIONAL_PURPOSE,
  SUBSCRIPTION_PURPOSE,
} from '../../constants/purposes';
import { validateCampaign } from '../ChatBlasterDetailsPanel/ChatBlasterDetailsPanel';
import CampaignButtonsSection from './CampaignButtonsSection/CampaignButtonsSection';
import './DripCampaignDetailsPanel.scss';
import { DripCampaignSteps } from './DripCampaignSteps/DripCampaignSteps';
import { mapStateToCampaignErrors } from './form-helpers';
import {
  InactiveDripAlert,
  useGetTextByCampaignReasons,
} from './InactiveDripAlert';
import ReadOnlyCampaignHeader from './ReadOnlyCampaignHeader/ReadOnlyCampaignHeader';
import { InactiveChannelAlert } from './InactiveChannelAlert';
import { useInactiveBotActivator } from '../../../../common/hooks/use-inactive-bot-activator';
import { useBotChannels } from '../../../../../common/hooks/use-bot-channels';
import { CampaignStatsDetail } from '../../components/DripCampaignTimelineItem/CampaignStatsDetail/CampaignStatsDetail';
import { useActivePageMessagingStatus } from '../../components/BlastTypeSelector/BlastTypeSelector';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';

type OuterProps = {
  campaignId?: number;
};
type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type ExternalProps = OuterProps &
  StoreProps &
  DispatchProps &
  RouterComponentProps;
type CampaignErrors = { campaignErrors: string[] };

type InitialState = ReturnType<typeof initialState>;
type StateUpdaters = StateUpdaterMethodTypes<
  InitialState,
  ExternalProps,
  typeof stateUpdaters
>;

type HandlerProps = ExternalProps & InitialState & StateUpdaters & LocalMethods;
type LocalMethods = {
  onChangeCampaignStatus: (status: CampaignStatuses) => void;
};

type HandlerMethods = LocalMethods &
  HandlerMethodTypes<HandlerProps, typeof handlers>;

type InnerProps = InitialState &
  StateUpdaters &
  HandlerMethods &
  CampaignErrors & {
    openPhonePreview: (dialogId: number) => void;
    currentDialog: number;
  };

export type DripCampaignProps = InnerProps & ExternalProps;

const mapStateToProps = (state: StoreState, props: OuterProps) => {
  const savedCampaign = campaignsWithMappedPurposeAndDisplayStatusSelector(
    state,
  ).find(campaign => campaign.id === props.campaignId);
  const savedCampaignActive = !!(
    savedCampaign && savedCampaign.displayStatus === 'Active'
  );
  const savedCampaignCanceled = !!(
    savedCampaign && savedCampaign.status === 'canceled'
  );
  const savedCampaignPaused = !!(
    savedCampaign && savedCampaign.status === 'paused'
  );
  const savedCampaignReadOnly =
    savedCampaignCanceled || savedCampaignActive || savedCampaignPaused;

  return {
    savedCampaign,
    savedCampaignCanceled,
    savedCampaignPaused,
    savedCampaignActive,
    savedCampaignReadOnly,
    audiences: state.audiences.entities,
    purposes: state.campaignPurposes.purposes,
    errors: state.campaigns.errors,
    dropdownPages: state.page.dropdown,
    welcomePage: welcomePageSelector(state),
    pageGroups: state.pageGroup.entities,
    chatbot: activeChatbotSelector(state),
    chatbotId: state.chatbot.activeChatbot && state.chatbot.activeChatbot.id,
    activeFacebookPage: activeFacebookPageSelector(state),
    activeFacebookPagePlanInfo: activeFacebookPagePlanInfoSelector(state).info,
    messagesStatistic: activeMessagesStatisticSelector(state),
    connectedPage: state.connectedPage.pageDetails,
    widgets: state.widget.entities,
    smsSupported:
      getTwilioAvailable(state) || companyFeatureFlagsSelector(state).twilio,
    hasSMSNumber: hasSMSNumberSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAudiences: (botId: number) =>
    dispatch(audiencesCRUDActions.retrieveAll(botId)),
  getCampaignPurposes: () =>
    dispatch(campaignPurposesCRUDActions.retrieveAll()),
  createAudience: (entity: Partial<AudiencesModel>, botId: number) =>
    dispatch(audiencesCRUDActions.create(entity, botId)),
  createDialogue: (entity: CreatePageParams) =>
    dispatch(pageCRUDActions.create(entity)),
  getCampaign: (id: number) => dispatch(campaignsCRUDActions.retrieve(id)),
  createCampaign: (campaign: CampaignType) =>
    dispatch(campaignsCRUDActions.create(campaign)),
  updateCampaign: (campaign: CampaignType, withoutRetrieveAll?: boolean) =>
    dispatch(
      campaignsCRUDActions.update(campaign, campaign.id, withoutRetrieveAll),
    ),
  openMonthlySendsWarningModal: () =>
    dispatch(modalActions.openMonthlySendsWarningModal()),
  openCampaignExceedsSendLimitModal: (
    campaignDisplayType: CampaignDisplayType,
    callToActionType: CallToActionType,
    targetAudienceSize?: number,
  ) =>
    dispatch(
      modalActions.openCampaignExceedsSendLimitModal(
        campaignDisplayType,
        callToActionType,
        targetAudienceSize,
      ),
    ),
  retrieveSubscriptionMessagingStatus: (pageId: number) =>
    dispatch(
      connectedPageCRUDActions.retrieveSubscriptionMessagingStatus(pageId),
    ),
  openGeneralConfirmModal: (props: GeneralConfirmModalProps) =>
    dispatch(modalActions.openGeneralConfirmModal(props)),
  openFeatureActivationCongratsModal: () =>
    dispatch(
      modalActions.openFeatureActivationCongratsModal({
        feature: 'drip_campaign',
      }),
    ),
});

const initialState = (props: ExternalProps) => {
  const { location, purposes, audiences, savedCampaign } = props;
  const selectedPurposeId = findPurposeId(
    purposes,
    (location?.state as { purpose?: string })?.purpose,
  );

  const includedAudienceIds = (
    location?.state as {
      includedAudienceIds: number[];
    }
  )?.includedAudienceIds;

  return {
    savedCampaignLoaded: false,
    name: '',
    queueModalOpen: false,
    selectedSchedulerId: -1,
    includedAudiences: [] as CampaignAudienceAttribute[],
    includedAudiencesIds: includedAudienceIds,
    excludedAudiences: [] as CampaignAudienceAttribute[],
    selectedPurposeId,
    schedulersAttributes: [] as CampaignSchedulerAttribute[],
    showConfirmEndModal: false,
    fetchComplete: false,
    acceptableTime: {} as CampaignAcceptableTime,
    platforms: { facebook: true, sms: false, instagram: false },
    blocked: true,
    allowRequeued: savedCampaign?.allowRequeued || false,
  };
};

export const originalAudienceIds = (
  kind: CampaignAudienceAttribute['kind'],
  campaign?: CampaignTypeWithMappedPurposeAndDisplayStatus,
) => {
  if (campaign && campaign.campaignAudiencesAttributes) {
    return campaign.campaignAudiencesAttributes
      .filter(a => a.kind === kind)
      .map(a => a.audienceId);
  }
  return [];
};

const onFetch =
  ({
    schedulersAttributes,
    setSchedulersAttributes,
    selectedSchedulerId,
  }: HandlerProps) =>
  (count: number) => {
    const schedulerIndex = schedulersAttributes.findIndex(
      s => s.id === selectedSchedulerId,
    );
    if (schedulerIndex === -1) {
      return;
    }
    const modifiedScheduler = {
      ...schedulersAttributes[schedulerIndex],
      currentQueuedCount: count,
    };
    setSchedulersAttributes(
      Object.assign([...schedulersAttributes], {
        [schedulerIndex]: modifiedScheduler,
      }),
    );
  };

const revertChanges = (state: InitialState, storeProps: StoreProps) => () => {
  const { savedCampaign, audiences, purposes } = storeProps;
  if (savedCampaign && savedCampaign.schedulersAttributes) {
    const selectedPurposeId = findPurposeId(
      purposes,
      savedCampaign.purposeName,
    );
    const includedAudienceIds = originalAudienceIds('included', savedCampaign);
    const excludedAudienceIds = originalAudienceIds('excluded', savedCampaign);
    return {
      savedCampaignLoaded: true,
      name: savedCampaign.name,
      includedAudiences: audiences
        .filter(a => includedAudienceIds.indexOf(a.id) > -1)
        .map(
          audience =>
            ({
              ...audience,
              audienceId: audience.id,
              name: audience.name,
              kind: 'included',
            } as CampaignAudienceAttribute),
        ),
      excludedAudiences: audiences
        .filter(a => excludedAudienceIds.indexOf(a.id) > -1)
        .map(
          audience =>
            ({
              ...audience,
              audienceId: audience.id,
              name: audience.name,
              kind: 'excluded',
            } as CampaignAudienceAttribute),
        ),
      schedulersAttributes: savedCampaign.schedulersAttributes,
      selectedPurposeId,
      acceptableTime: savedCampaign.acceptableTime,
    };
  }
};

const stateUpdaters = {
  setSavedCampaignLoaded: () => (savedCampaignLoaded: boolean) => ({
    savedCampaignLoaded,
  }),
  setName: () => (name: string) => ({ name }),
  setIncludedAudiences:
    () => (includedAudiences: CampaignAudienceAttribute[]) => ({
      includedAudiences,
    }),
  setExcludedAudiences:
    () => (excludedAudiences: CampaignAudienceAttribute[]) => ({
      excludedAudiences,
    }),
  setSchedulersAttributes:
    () => (schedulersAttributes: CampaignSchedulerAttribute[]) => ({
      schedulersAttributes,
    }),
  setShowConfirmEndModal: () => (showConfirmEndModal: boolean) => ({
    showConfirmEndModal,
  }),
  setFetchComplete: () => (fetchComplete: boolean) => ({ fetchComplete }),
  setQueueModalOpen: () => (queueModalOpen: boolean) => ({ queueModalOpen }),
  setSelectedSchedulerId: () => (selectedSchedulerId: number) => ({
    selectedSchedulerId,
  }),
  setSelectedPurposeId: () => (selectedPurposeId: number) => ({
    selectedPurposeId,
  }),
  setAcceptableTime: () => (acceptableTime: CampaignAcceptableTime) => ({
    acceptableTime,
  }),
  setPlatforms:
    () =>
    (platforms: { facebook: boolean; sms: boolean; instagram: boolean }) => ({
      platforms,
    }),
  setAllowRequeueing: () => (allowRequeued: boolean) => ({
    allowRequeued,
  }),
  setBlocked: () => (blocked: boolean) => ({ blocked }),
  revertChanges,
};

async function componentDidMount(this: LifeCycleThis<DripCampaignProps>) {
  const {
    getAudiences,
    getCampaign,
    chatbot,
    campaignId,
    savedCampaign,
    getCampaignPurposes,
    createDefaultAudience,
    setFetchComplete,
  } = this.props;

  getCampaignPurposes();

  if (
    campaignId &&
    (!savedCampaign ||
      savedCampaign.id !== campaignId ||
      !savedCampaign.schedulersAttributes)
  ) {
    await getCampaign(campaignId);
  }

  if (chatbot) {
    const audiences = await getAudiences(chatbot.id);

    if (audiences && !audiences.audiences.length) {
      await createDefaultAudience();
    }
  }

  setFetchComplete(true);
}

function componentDidUpdate(
  this: LifeCycleThis<DripCampaignProps>,
  prevProps: DripCampaignProps,
) {
  const {
    audiences,
    chatbot,
    campaignId,
    savedCampaign,
    getAudiences,
    getCampaign,
    revertChanges,
    setPlatforms,
    setExcludedAudiences,
    setIncludedAudiences,
    savedCampaignLoaded,
    fetchComplete,
  } = this.props;

  if (chatbot && (!prevProps.chatbot || chatbot.id !== prevProps.chatbot.id)) {
    getAudiences(chatbot.id);
  }
  if (campaignId && campaignId !== prevProps.campaignId) {
    getCampaign(campaignId);
    return;
  }
  if (
    campaignId &&
    !savedCampaignLoaded &&
    savedCampaign &&
    savedCampaign.schedulersAttributes
  ) {
    revertChanges();
    const platforms = { facebook: false, sms: false, instagram: false };
    if (savedCampaign && savedCampaign.platforms) {
      savedCampaign.platforms.forEach(platform => {
        platforms[platform] = true;
      });
    }
    setPlatforms(platforms);
  }

  if (fetchComplete && savedCampaign && !prevProps.fetchComplete) {
    if (savedCampaign.campaignAudiencesAttributes) {
      const partitioned = savedCampaign.campaignAudiencesAttributes.reduce(
        (out, audienceAttribute) => {
          const { kind } = audienceAttribute;
          if (kind) {
            out[kind].push(audienceAttribute);
          }
          return out;
        },
        {
          included: [] as CampaignAudienceAttribute[],
          excluded: [] as CampaignAudienceAttribute[],
        },
      );

      if (partitioned.included.length) {
        setIncludedAudiences(partitioned.included);
      }

      if (partitioned.excluded.length) {
        setExcludedAudiences(partitioned.excluded);
      }
    }
  }
}

const appendDestroyAudienceAttributes = (
  savedCampaign: CampaignType,
  entity: CampaignType,
) => {
  savedCampaign.campaignAudiencesAttributes?.forEach(attribute => {
    const found = entity.campaignAudiencesAttributes?.some(
      v => v.audienceId === attribute.audienceId,
    );

    if (!found) {
      entity.campaignAudiencesAttributes?.push({
        ...attribute,
        destroy: true,
      });
    }
  });
};

const getCampaignEntity = (props: HandlerProps, alsoStart: boolean) => {
  const {
    chatbot,
    name,
    includedAudiences,
    excludedAudiences,
    schedulersAttributes,
    selectedPurposeId,
    savedCampaign,
    acceptableTime,
    platforms,
    allowRequeued,
  } = props;

  if (!chatbot) {
    return null;
  }

  const entity = {} as CampaignType;
  entity.active = alsoStart;
  entity.campaignAudiencesAttributes = [
    ...includedAudiences.map(
      a => ({ ...a, kind: 'included' } as CampaignAudienceAttribute),
    ),
    ...excludedAudiences.map(
      a => ({ ...a, kind: 'excluded' } as CampaignAudienceAttribute),
    ),
  ];
  entity.scheduleType = 'drip';
  entity.botId = chatbot.id;
  entity.name = name;
  entity.schedulersAttributes = schedulersAttributes;
  entity.purpose = selectedPurposeId;
  entity.acceptableTime = acceptableTime;
  entity.platforms = getPlatforms(platforms);
  entity.allowRequeued = allowRequeued;

  if (
    acceptableTime &&
    acceptableTime.daysOfWeek &&
    acceptableTime.daysOfWeek.length === 0
  ) {
    entity.acceptableTime = { ...acceptableTime, daysOfWeek: EVERY_DAY };
  }

  if (savedCampaign) {
    entity.id = savedCampaign.id;
    entity.active = savedCampaign.active || alsoStart;
    appendDestroyAudienceAttributes(savedCampaign, entity);
  }
  return entity;
};

const findPurposeId = (
  purposes: CampaignPurpose[],
  purpose: string | undefined,
  defaultPurposeId: number = PROMOTIONAL_PURPOSE,
): number => {
  if (purposes.length && purpose) {
    const selectedPurpose = purposes.find(item => item.name === purpose);
    if (selectedPurpose) {
      return selectedPurpose.id;
    }
  }
  return defaultPurposeId;
};

const getPlatforms = (platformMap: {
  facebook: boolean;
  sms: boolean;
  instagram: boolean;
}): PlatformType[] => {
  const platforms: PlatformType[] = [];

  if (platformMap.facebook) {
    platforms.push('facebook');
  }

  if (platformMap.sms) {
    platforms.push('sms');
  }

  if (platformMap.instagram) {
    platforms.push('instagram');
  }

  return platforms;
};

const DripCampaignDetailsPanel = (props: DripCampaignProps) => {
  const {
    savedCampaign,
    savedCampaignCanceled,
    savedCampaignPaused,
    savedCampaignActive,
    name,
    setName,
    includedAudiences,
    selectedPurposeId,
    errors,
    excludedAudiences,
    chatbot,
    onSetPause,
    showConfirmEndModal,
    setShowConfirmEndModal,
    onEndCampaign,
    redirectToIndex,
    fetchComplete,
    campaignErrors,
    chatbotId,
    queueModalOpen,
    setQueueModalOpen,
    selectedSchedulerId,
    acceptableTime,
    onFetch,
    handleNavigation,
    blocked,
    platforms,
    activeFacebookPage,
    activeFacebookPagePlanInfo,
    openCampaignExceedsSendLimitModal,
    updateCampaign,
    createCampaign,
    setBlocked,
    openFeatureActivationCongratsModal,
    openMonthlySendsWarningModal,
    openGeneralConfirmModal,
    widgets,
    revertChanges,
    setSchedulersAttributes,
    setAllowRequeueing,
  } = props;

  const [dialogNumberId, setDialogNumberId] = useState(-1);
  const [isRequesting, setIsRequesting] = useState(false);

  const isNewBillingSubscription =
    activeFacebookPage?.subscription &&
    isBilling2023Subscription(activeFacebookPage.subscription);

  const { isMobile } = getDeviceInfo();

  const containerClassName = classNames('drip-campaign-detail');

  const pathGraph = useNewGraphInstance(dialogNumberId);
  const { activateBotGuard } = useInactiveBotActivator();
  const { validateChannels, activateChannelGuard } = useBotChannels();
  const { isMessagingApproved } = useActivePageMessagingStatus();

  const canSendDrips = useSelector(
    combinedFeatureFlagsSelector,
  ).campaignsDripsSend;
  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;

  const modalActions = useModalActions();

  const disApprovedMessaging = !!(
    selectedPurposeId === SUBSCRIPTION_PURPOSE && isMessagingApproved
  );

  const purposeIsPromotional = selectedPurposeId === PROMOTIONAL_PURPOSE;
  const campaignInvalid = !!campaignErrors.length;

  const { isFreeTrialPromotionWithoutSubscription } =
    useFreeTrialUpgradeModal();
  const history = useHistory();

  const alertText = useGetTextByCampaignReasons(
    savedCampaign?.cancelReason,
    savedCampaign?.pauseReason,
  );

  if (!fetchComplete) {
    return null;
  }

  const openPhonePreview = (dialogId: number) => {
    setDialogNumberId(dialogId);
  };

  const onSaveCampaign = async (
    alsoStart: boolean,
    withoutRedirect?: boolean,
    withoutRetrieveAllCampaign?: boolean,
  ) => {
    const campaign = getCampaignEntity(props, alsoStart);

    if (!campaign) {
      return;
    }

    const audiencesContactCount =
      getAudiencesContactCount(includedAudiences) || 1;
    const messagesRemaining = activeFacebookPage
      ? activeFacebookPage.messagesStatistic.messagesRemaining
      : Number.MAX_SAFE_INTEGER;

    if (activeFacebookPagePlanInfo.isFreePlan && messagesRemaining === 0) {
      return openMonthlySendsWarningModal();
    }

    if (
      !(
        activeFacebookPagePlanInfo.isCustomTeam ||
        activeFacebookPagePlanInfo.isLegacyTeamPlan
      ) &&
      messagesRemaining < audiencesContactCount &&
      !(activeFacebookPage && activeFacebookPage.overagesEnabled) &&
      alsoStart
    ) {
      const isUnpaid =
        (activeFacebookPagePlanInfo.isFreePlan ||
          isFreeTrialPromotionWithoutSubscription) &&
        !isNewBillingSubscription;

      if (isUnpaid) {
        return openCampaignExceedsSendLimitModal('Drip Campaign', 'upgrade');
      }

      if (savedCampaign?.status !== 'paused' && !isNewBillingSubscription) {
        return openCampaignExceedsSendLimitModal(
          'Drip Campaign',
          'enable_overage',
          audiencesContactCount,
        );
      }
    }

    if (alsoStart) {
      campaign.status = 'queued';
    }

    try {
      setIsRequesting(true);
      const response = await (campaign.id
        ? updateCampaign(campaign, withoutRetrieveAllCampaign)
        : createCampaign(campaign));
      setBlocked(false);
      if (!withoutRedirect) {
        setTimeout(redirectToIndex, 10);
      } else {
        revertChanges();
        setSchedulersAttributes(response.campaign?.schedulersAttributes);
      }
      showActivationCues &&
        isFreeTrialPromotionWithoutSubscription &&
        setTimeout(openFeatureActivationCongratsModal, 100);
      return response.campaign;
    } catch {
      // Form will show errors.
    } finally {
      setIsRequesting(false);
    }
  };

  const campaignPlatforms = [
    platforms.facebook && 'facebook',
    platforms.sms && 'sms',
    platforms.instagram && 'instagram',
  ].filter(Boolean);

  const onSave = async (
    alsoStart: boolean,
    withoutRedirect?: boolean,
    withoutRetrieveAllCampaign?: boolean,
  ) => {
    const activateHandler = () => {
      if (alsoStart) {
        activateBotGuard()
          .then(() => activateChannelGuard(campaignPlatforms))
          .then(() =>
            onSaveCampaign(
              alsoStart,
              withoutRedirect,
              withoutRetrieveAllCampaign,
            ),
          )
          .catch(() => {}); // handled in the guard
      } else {
        return onSaveCampaign(
          alsoStart,
          withoutRedirect,
          withoutRetrieveAllCampaign,
        );
      }
    };

    if (!canSendDrips && platforms.sms) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames.DRIP_CAMPAIGN_ACTIVATE_BUTTON,
        feature: 'campaignsDripsSend',
      });
      return;
    }

    if (
      (alsoStart && widgets.find(widget => !widget.validated.facebook)) ||
      !widgets.length
    ) {
      openGeneralConfirmModal({
        headerText: 'Are you sure you want to start this campaign?',
        messageText: !widgets.length ? (
          <div>
            Your dialogue is empty! We recommend that you add widgets before
            starting your Drip Campaign
          </div>
        ) : (
          <div>
            Your dialogue contains errors! We recommend that you fix the errors
            before starting your Drip Campaign
          </div>
        ),
        okButtonText: 'Send anyway',
        okButtonStyling: { gray: true },
        cancelButtonText: 'Cancel',
        cancelButtonColor: 'blue',
        cancelButtonFirst: true,
        onCloseHandler: (ok: boolean) => ok && activateHandler(),
      });
    } else {
      return activateHandler();
    }
  };

  const validationErrors = validateCampaign({
    name,
    audienceId: includedAudiences.length,
    platforms: campaignPlatforms,
  } as CampaignType);

  const invalidChannels = validateChannels(
    campaignPlatforms.filter(v => typeof v === 'string') as ChannelName[],
  );

  return (
    <>
      <Prompt when={blocked} message={handleNavigation} />
      <div className={containerClassName}>
        {alertText && <InactiveDripAlert text={alertText} />}
        {!!invalidChannels.length &&
          invalidChannels.map(channel => (
            <InactiveChannelAlert channel={channel} type="drip campaign" />
          ))}
        <div className="drip-campaign-detail__title-area">
          <div
            className="drip-campaign-detail__back-button"
            onClick={() => {
              if (chatbotId) {
                history.push(
                  BotBuilderRoutes.campaigns.getLink({
                    chatbotId,
                    kind: 'drip_campaigns',
                  }),
                );
              }
            }}
          >
            <AngleLeftLightIcon />
            {!isMobile && 'Back'}
          </div>
          <div className="drip-campaign-detail__main-header">
            <TextInput
              placeholder="Drip Campaign Name"
              disabled={savedCampaignCanceled}
              value={name}
              onChange={e => setName(e.target.value)}
              className="drip-campaign-detail__name-input"
              maxLength={100}
            />

            {validationErrors.name && (
              <Alert
                className="drip-campaign-detail__error"
                type="error"
                showCloseIcon={false}
              >
                {validationErrors.name}
              </Alert>
            )}

            <div className="drip-campaign-detail__header-right-section">
              <NeedHelpLabel
                iconOnly={isMobile}
                style={isMobile ? { marginRight: '0px' } : {}}
                link="https://customers.ai/help/article/h9ckxgr6eh-creating-a-drip-campaign-video"
              />

              {savedCampaign && !isMobile && (
                <CampaignStatusIcon
                  campaign={savedCampaign}
                  showOnlyStatus={false}
                  className="drip-campaign-detail__status-icon"
                />
              )}

              {savedCampaignCanceled && chatbot ? (
                <ReadOnlyCampaignHeader
                  name={name}
                  chatbot={chatbot}
                  excludedAudiences={excludedAudiences}
                  includedAudiences={includedAudiences}
                  acceptableTime={acceptableTime}
                />
              ) : (
                <CampaignButtonsSection
                  purposeIsPromotional={purposeIsPromotional}
                  savedCampaign={savedCampaign}
                  savedCampaignActive={savedCampaignActive}
                  savedCampaignPaused={savedCampaignPaused}
                  savedCampaignCanceled={savedCampaignCanceled}
                  setShowConfirmEndModal={setShowConfirmEndModal}
                  onSaveCampaign={onSave}
                  onSetPause={onSetPause}
                  disApprovedMessaging={disApprovedMessaging}
                  campaignErrors={campaignErrors}
                  botIsTemplate={!!chatbot.template}
                  isRequesting={isRequesting}
                />
              )}
            </div>
            {savedCampaign && isMobile && (
              <CampaignStatusIcon
                campaign={savedCampaign}
                showOnlyStatus={true}
                className="drip-campaign-detail__status-icon"
              />
            )}
          </div>
        </div>

        <PathGraphContext.Provider value={pathGraph}>
          <div className="drip-campaign-detail__main-container">
            <DripCampaignSteps
              {...props}
              openPhonePreview={openPhonePreview}
              currentDialog={dialogNumberId}
              errors={validationErrors}
              onSaveCampaign={() => onSave(false, true, true)}
            />

            {/*<DialoguePreview className="drip-campaign-detail__preview" dialogueId={dialogNumberId} sticky style={{ top: 30 }} />*/}
          </div>
        </PathGraphContext.Provider>

        <CampaignQueueModal
          isOpen={queueModalOpen}
          onClose={() => setQueueModalOpen(false)}
          onFetch={onFetch}
          schedulerId={selectedSchedulerId}
          campaign={
            savedCampaign as CampaignTypeWithMappedPurposeAndDisplayStatus
          }
        />

        <ConfirmEndModal
          open={showConfirmEndModal}
          setOpen={setShowConfirmEndModal}
          onEndCampaign={onEndCampaign}
        />
      </div>
    </>
  );
};

// TODO: Remove when BE supports sending to all contacts when no audience is selected
const createDefaultAudience =
  ({ createAudience, chatbot, getAudiences }: HandlerProps) =>
  async () => {
    if (chatbot) {
      await createAudience(
        { name: 'All Contacts', filterParams: {} },
        chatbot.id,
      );
      getAudiences(chatbot.id);
    }
  };

const redirectToIndex =
  ({ history, chatbot }: HandlerProps) =>
  () => {
    if (chatbot) {
      const path = BotBuilderRoutes.campaigns.getLink({
        chatbotId: chatbot.id,
        kind: 'drip_campaigns',
      });

      if (path) {
        history.push(path);
      }
    }
  };

const onEndCampaign =
  ({ onChangeCampaignStatus }: HandlerProps) =>
  async () => {
    onChangeCampaignStatus('canceled');
  };

const onSetPause =
  ({ onChangeCampaignStatus }: HandlerProps) =>
  async () => {
    onChangeCampaignStatus('paused');
  };

const onChangeCampaignStatus =
  (props: HandlerProps) => async (status: CampaignStatuses) => {
    const { updateCampaign, setBlocked } = props;
    const campaign = getCampaignEntity(props, false);
    if (!campaign || !campaign.id) {
      return;
    }
    campaign.status = status;
    try {
      await updateCampaign(campaign);
      setBlocked(false);
      setTimeout(() => redirectToIndex(props)(), 10);
    } catch {
      // Form will show errors.
    }
  };

const getAudiencesContactCount = (
  includedAudiences: CampaignAudienceAttribute[],
) => {
  return includedAudiences.reduce((totalAudienceCount, audience) => {
    const contactsCount = audience.contactsCount || 0;
    return totalAudienceCount + contactsCount;
  }, 0);
};

const onChangeScheduler =
  (props: HandlerProps) => (scheduler: CampaignSchedulerAttribute) => {
    const { schedulersAttributes, setSchedulersAttributes } = props;
    setSchedulersAttributes(
      schedulersAttributes.map((s, i) =>
        i === scheduler.sortOrder ? scheduler : s,
      ),
    );
  };

const deleteScheduler =
  (props: HandlerProps) => (scheduler: CampaignSchedulerAttribute) => {
    const { schedulersAttributes, setSchedulersAttributes } = props;
    const updatedAttrs = [...schedulersAttributes];
    updatedAttrs.splice(scheduler.sortOrder, 1);
    if (scheduler.id) {
      // Retain schedulers that are saved in BE so we send a destroy command
      updatedAttrs.push({ ...scheduler, destroy: true });
    }
    setSchedulersAttributes(
      updatedAttrs.map((s, i) => ({ ...s, sortOrder: i })),
    );
  };

const onAddTimelineItem = (props: HandlerProps) => async () => {
  const {
    schedulersAttributes,
    setSchedulersAttributes,
    createDialogue,
    chatbotId,
  } = props;

  if (chatbotId) {
    const response = await createDialogue({
      botId: chatbotId,
      name: 'Start',
      kind: PageKind.StartBot,
      chatStarterKind: 'drip_campaign',
    });

    if (response && response.success) {
      const { page } = response;
      const blankMessage = {
        destinationPageId: page.id,
        destinationPageName: page.name,
        destinationPageSearchToken: page.searchToken,
        intervalInMin: 1,
        sortOrder: schedulersAttributes.length,
      };

      setSchedulersAttributes([...schedulersAttributes, blankMessage]);
    }
  }
};
const onDragEnd =
  ({ schedulersAttributes, setSchedulersAttributes }: HandlerProps) =>
  (result: DropResult) => {
    // result.
    if (result.destination) {
      const attrs = reorder(
        schedulersAttributes,
        result.source.index,
        result.destination.index,
      ).map((s, i) => ({ ...s, sortOrder: i }));
      setSchedulersAttributes(attrs);
    }
  };

const handleNavigation =
  (props: HandlerProps) =>
  (location: Location): boolean => {
    const { openGeneralConfirmModal, setBlocked, history } = props;
    if (hasUnsavedChanges(props)) {
      setTimeout(() => {
        openGeneralConfirmModal({
          headerText: 'Are you sure you want to leave this page?',
          messageText: (
            <div>
              Hey there! It looks like you&apos;re in the middle of writing
              something and you haven&apos;t saved all of your content.
              <br />
              <br />
              Save before you go!
            </div>
          ),
          okButtonText: 'Leave',
          cancelButtonText: 'Stay',
          cancelButtonFirst: true,
          onCloseHandler: (ok: boolean) => {
            if (ok) {
              setBlocked(false);
              setTimeout(
                () => history.push(location.pathname + location.search),
                10,
              );
            }
          },
        });
      }, 500);
      return false;
    }

    return true;
  };

const hasUnsavedChanges = (props: HandlerProps) => {
  const {
    savedCampaign,
    name,
    purposes,
    selectedPurposeId,
    includedAudiences,
    excludedAudiences,
    schedulersAttributes,
    acceptableTime,
  } = props;

  if (!savedCampaign) return true;

  const savedPurposeId = findPurposeId(purposes, savedCampaign.purposeName);
  const savedIncludedAudienceIds = originalAudienceIds(
    'included',
    savedCampaign,
  );
  const savedExcludedAudienceIds = originalAudienceIds(
    'excluded',
    savedCampaign,
  );
  const includedAudiencesIds = includedAudiences.map(aud => aud.audienceId);
  const excludedAudiencesIds = excludedAudiences.map(aud => aud.audienceId);

  if (
    name !== savedCampaign.name ||
    savedPurposeId !== selectedPurposeId ||
    !isEqual(includedAudiencesIds.sort(), savedIncludedAudienceIds.sort()) ||
    !isEqual(excludedAudiencesIds.sort(), savedExcludedAudienceIds.sort()) ||
    !isEqual(acceptableTime, savedCampaign.acceptableTime) ||
    !isEqual(schedulersAttributes, savedCampaign.schedulersAttributes)
  ) {
    return true;
  }

  return false;
};

const handlers = {
  redirectToIndex,
  onEndCampaign,
  onSetPause,
  onChangeScheduler,
  onAddTimelineItem,
  createDefaultAudience,
  onDragEnd,
  onFetch,
  deleteScheduler,
  handleNavigation,
};

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withProps(mapStateToCampaignErrors),
  withHandlers({ onChangeCampaignStatus }),
  withHandlers(handlers),
  lifecycle<DripCampaignProps, {}>({
    componentDidUpdate,
    componentDidMount,
  }),
);

const OldDrip = enhance(DripCampaignDetailsPanel);

export default (props: OuterProps) => {
  const { campaignId } = props;
  const { campaignId: routeCampaignId } = useRouteParams<{
    campaignId?: string;
  }>();

  const router = useRouter();

  const [fetching, setFetching] = useState(true);

  // Responses from collection and resource views from API are different
  // Old solution relies on campaigns saved in redux which might miss needed data
  // Here we perform request to get up to date data with all actual details
  const campaignsCRUDActions = useCampaignCRUDActions();
  useEffect(() => {
    (async () => {
      const id = campaignId || routeCampaignId;
      if (id) {
        await campaignsCRUDActions.retrieve(Number(id));
        setFetching(false);
      }
    })();
  }, []);

  if (fetching) {
    return <></>;
  }

  return (
    <Switch>
      <Route
        path={`${router.match.path}/stats/:schedulerId?`}
        component={CampaignStatsDetail}
      />
      <Route path={router.match.path} render={() => <OldDrip {...props} />} />
    </Switch>
  );
};
